import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, useIntl } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import OkCancelModal from '@/shared/components/OkCancelModal';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import api from '@/shared/api';
import config from '@/config';
import ToastrService from '@/shared/services/ToastrService';
import TaskTemplateFilter, { MAIN_TASK_ONLY } from '@/modules/tasks/TaskTemplateFilter';
import Checkbox from '@/shared/components/input/Checkbox';
import enumCache from '@/shared/services/EnumCacheService';
import { dynamicSort } from '@/shared/utils/dynamicSort';
import classNames from 'classnames';
import authService, { privileges } from '@/shared/services/AuthService';
import AutoComplete from '@/shared/components/input/select/AutoComplete';
import TranslateListItem from '../../../shared/components/translations/TranslateListItem';
import translationHelper from '../../../shared/components/translations/TranslationHelper';
import DataCacheService from '@/shared/services/DataCacheService';
import handleHttpError from '../../../shared/services/HandleHttpError';
import { EMAIL_ACTION_WORKFLOW_TYPE } from './WorkflowConfigurationContainer';
import messages from './workflowConfigurationMessages';

class WorkflowConfigurationModal extends Component {
  static propTypes = {
    show: PropTypes.bool,
    workflowConfiguration: PropTypes.shape({}),
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    isEmailAction: PropTypes.bool,
    intl: intlShape.isRequired,
  };

  static defaultProps = {
    show: false,
    isEmailAction: false,
    workflowConfiguration: null,
  };

  constructor(props) {
    super(props);
    const { workflowConfiguration, isEmailAction } = this.props;

    const defaultViewModel = {
      workflowType: isEmailAction ? EMAIL_ACTION_WORKFLOW_TYPE : null,
      desiredStatus: '',
      mainTaskTemplateId: null,
      subTaskTemplateId: null,
      toleranceDaysAfter: 30,
      toleranceDaysBefore: 0,
      requireDocumentSigned: false,
      name: '',
    };

    const initialViewModel = workflowConfiguration
      ? { name: '', ...workflowConfiguration }
      : defaultViewModel;
    if (
      workflowConfiguration &&
      workflowConfiguration.mainTaskTemplateId &&
      !workflowConfiguration.subTaskTemplateId
    ) {
      initialViewModel.subTaskTemplateId = MAIN_TASK_ONLY;
    }

    this.state = {
      viewModel: initialViewModel,
      showValidation: false,
      workflowTypes: [],
      translations: null,
      previewLanguage: null,
      previewCompanyId: null,
      companies: [],
    };
  }

  componentDidMount() {
    const { intl, isEmailAction, workflowConfiguration } = this.props;
    const { viewModel } = this.state;
    enumCache.get('workflowType').then(data => {
      let selectedWorkflow;
      const mappedWorkflows = data
        .filter(x => isEmailAction || x.key !== EMAIL_ACTION_WORKFLOW_TYPE)
        .map(x => ({
          id: x.key,
          label: intl.formatMessage(listItemMsg('workflowType', x.key, x.value)),
        }))
        .sort(dynamicSort('label'));
      if (viewModel.workflowType >= 0) {
        selectedWorkflow = mappedWorkflows
          .map(x => ({ key: x.id, label: x.label }))
          .find(x => x.key === viewModel.workflowType);
      }
      this.setState({
        selectedWorkflow,
        workflowTypes: mappedWorkflows,
      });
    });

    if (isEmailAction) {
      const allCompanies = DataCacheService.getDataCache()?.company || [];
      const companies = allCompanies
        .map(c => ({ id: c.id, name: c.name }))
        .sort(dynamicSort('name'));
      const previewCompanyId = DataCacheService.getDefaultCompany()?.id || null;
      this.setState({ companies, previewCompanyId });

      const translationsPromise = workflowConfiguration?.id
        ? translationHelper.retrieve(
            'workflowConfiguration',
            workflowConfiguration.id,
            workflowConfiguration.name,
          )
        : translationHelper.create('workflowConfiguration');

      translationsPromise.then(translations => {
        this.setState({ translations });
      });
    }
  }

  closeModal = () => {
    const { onClose } = this.props;

    onClose();
  };

  saveModal = () => {
    const { onSave, intl, isEmailAction } = this.props;
    const { viewModel, translations } = this.state;
    const configurationDto = { ...viewModel };
    const successMessage = intl.formatMessage(messages.ChangesSaved);

    if (configurationDto.subTaskTemplateId === MAIN_TASK_ONLY) {
      configurationDto.subTaskTemplateId = null;
    }
    if (configurationDto.workflowType === 3) {
      configurationDto.desiredStatus = null; // remove desired status for VAT amount workflow
    }
    // Validate
    const missingName = isEmailAction && !configurationDto.name?.trim();
    const invalidTranslations = translations && !translationHelper.validate(translations);
    if (
      !configurationDto.mainTaskTemplateId ||
      (!viewModel.workflowType && viewModel.workflowType !== 0) ||
      missingName ||
      invalidTranslations
    ) {
      this.setState(state => ({
        ...state,
        showValidation: true,
      }));
      return;
    }

    const action = configurationDto.id === undefined ? api.base.post : api.base.put;

    return action(`${config.appUrls.api}/api/workflowconfiguration`, configurationDto).then(
      ({ data: serviceObject }) => {
        const saveResult = () => {
          ToastrService.success(successMessage);
          onSave(serviceObject);
        };

        if (isEmailAction) {
          if (configurationDto.id === undefined) {
            return translationHelper
              .save(
                translationHelper.setKey(translations, 'workflowConfiguration', serviceObject.id),
              )
              .then(saveResult);
          } else {
            return translationHelper.save(translations).then(saveResult);
          }
        }

        return saveResult();
      },
      error => {
        handleHttpError(intl)(error);
      },
    );
  };

  selectedWorkflowChanged = workflow => {
    if (workflow === undefined) return;
    const { viewModel } = this.state;
    this.setState({
      selectedWorkflow: workflow,
      viewModel: {
        ...viewModel,
        workflowType: workflow ? parseInt(workflow.key) : undefined,
      },
    });
  };

  updateTranslations = translations => {
    this.setState({ translations });
  };

  changeName = e => {
    this.setState(state => ({ viewModel: { ...state.viewModel, name: e.target.value || name } }));
  };

  render() {
    const { show, intl, isEmailAction } = this.props;
    const {
      viewModel,
      showValidation,
      workflowTypes,
      selectedWorkflow,
      translations,
      previewLanguage,
      previewCompanyId,
      companies,
    } = this.state;
    const isNew = !viewModel.id;
    const showSubtaskDetails = !viewModel.workflowType || viewModel.workflowType !== 3;
    const showReferenceDate =
      (!viewModel.workflowType || viewModel.workflowType !== 58) && !isEmailAction;
    const workFlowTypeClassName = classNames('form-group', {
      'has-error': showValidation && !viewModel.workflowType && viewModel.workflowType !== 0,
    });

    const nameClassName = classNames('form-group', {
      'has-error': showValidation && !viewModel.name && viewModel.workflowType === 161,
    });
    const canUpdate = authService.hasPermission({
      functionality: 'settings',
      privilege: privileges.Update,
    });

    const mappedWorkflows = workflowTypes.map(x => ({
      key: x.id,
      label: x.label,
    }));

    const isPitMandatesTaxReturnTypeTaskflow = selectedWorkflow?.key === 144;
    const needsSubtaskChoice =
      showSubtaskDetails && viewModel.mainTaskTemplateId && !viewModel.subTaskTemplateId;
    return (
      <OkCancelModal
        title={intl.formatMessage(messages.TaskflowAction)}
        show={show}
        disabled={!canUpdate || needsSubtaskChoice}
        onClose={this.closeModal}
        onSave={this.saveModal}
        size={isEmailAction ? 'xl' : ''}
      >
        {show && (
          <div style={isEmailAction ? { display: 'flex', gap: '24px' } : undefined}>
            <form ref={this.form} style={isEmailAction ? { flex: 1, minWidth: 0 } : undefined}>
              <div className="form-horizontal">
                {isEmailAction && (
                  <div className={nameClassName}>
                    <label className="control-label col-md-4">
                      {intl.formatMessage(messages.Name)}
                    </label>
                    <div className="col-md-8">
                      <input
                        type="text"
                        className="form-control"
                        value={viewModel.name || ''}
                        maxLength={255}
                        onChange={this.changeName}
                        placeholder={intl.formatMessage(messages.Name)}
                      />
                    </div>
                  </div>
                )}
                {isEmailAction && <hr />}
                {isEmailAction && translations && (
                  <TranslateListItem
                    translations={translations}
                    label={intl.formatMessage(messages.ActionName)}
                    labelClass="col-md-4"
                    inputClass="col-md-8"
                    onChange={this.updateTranslations}
                    showValidation={showValidation}
                    maxLength={255}
                  />
                )}

                {isEmailAction && (
                  <div className="form-group">
                    <div className="col-md-offset-4 col-md-8">
                      <span className="text-muted">
                        {intl.formatMessage(messages.ActionWorkflowNameInfo)}
                      </span>
                    </div>
                  </div>
                )}
                {!isEmailAction && (
                  <div className={workFlowTypeClassName}>
                    <label className="control-label col-md-4">
                      {intl.formatMessage(messages.Document)}
                    </label>
                    <div className="col-md-8">
                      <AutoComplete
                        items={mappedWorkflows}
                        placeholder={intl.formatMessage(messages.StartTypingToSearch)}
                        onChange={item => this.selectedWorkflowChanged(item)}
                        mode="edit"
                        value={selectedWorkflow}
                        showAll
                      />
                    </div>
                  </div>
                )}
                {!isEmailAction && (
                  <div className="form-group">
                    <label className="control-label col-md-4">
                      {intl.formatMessage(messages.RequireDocumentSigned)}
                    </label>
                    <div className="col-md-8 margin5T">
                      <Checkbox
                        onClick={value =>
                          this.setState({
                            viewModel: {
                              ...viewModel,
                              requireDocumentSigned: value,
                            },
                          })
                        }
                        value={viewModel.requireDocumentSigned}
                      />
                    </div>
                  </div>
                )}
                <hr />
                {showSubtaskDetails &&
                  !isPitMandatesTaxReturnTypeTaskflow &&
                  viewModel.mainTaskTemplateId &&
                  viewModel.subTaskTemplateId === MAIN_TASK_ONLY && (
                    <div className="form-group">
                      <div className="col-md-offset-4 col-md-8">
                        {intl.formatMessage(messages.AllSubtasksWillBeSetToDoneIfYouDontSelectOne)}
                      </div>
                    </div>
                  )}
                {isPitMandatesTaxReturnTypeTaskflow &&
                  viewModel.mainTaskTemplateId &&
                  viewModel.subTaskTemplateId === MAIN_TASK_ONLY && (
                    <div className="form-group">
                      <div className="col-md-offset-4 col-md-8">
                        {intl.formatMessage(
                          messages.TheDeadlineOfTheSubtasksWillBeAdjustedAsConfiguredInTaskTemplate,
                        )}
                      </div>
                    </div>
                  )}
                <TaskTemplateFilter
                  showValidation={showValidation || needsSubtaskChoice}
                  mainTaskTemplateId={viewModel.mainTaskTemplateId}
                  subTaskTemplateId={viewModel.subTaskTemplateId}
                  showSubtaskFilter={showSubtaskDetails}
                  showMainTaskOnlyOption
                  onMainTaskTemplateChanged={e =>
                    this.setState({
                      viewModel: {
                        ...viewModel,
                        mainTaskTemplateId: e,
                        subTaskTemplateId: null,
                      },
                    })
                  }
                  onSubTaskTemplateChanged={e =>
                    this.setState({
                      viewModel: {
                        ...viewModel,
                        subTaskTemplateId: e || null,
                      },
                    })
                  }
                />

                {showSubtaskDetails && !isPitMandatesTaxReturnTypeTaskflow && (
                  <React.Fragment>
                    <div className="form-group">
                      <label className="control-label col-md-4">
                        {intl.formatMessage(messages.NewTaskStatus)}
                      </label>
                      <div className="col-md-8">
                        <select
                          onChange={e =>
                            this.setState({
                              viewModel: {
                                ...viewModel,
                                desiredStatus: e.target.value,
                              },
                            })
                          }
                          className="form-control"
                          value={viewModel.desiredStatus}
                        >
                          <option value="">
                            {' '}
                            {intl.formatMessage(messages.DontChangeStatus)}{' '}
                          </option>
                          <option value={0}>
                            {intl.formatMessage(listItemMsg('taskstatus', 0, 'To do'))}
                          </option>
                          <option value={1}>
                            {intl.formatMessage(listItemMsg('taskstatus', 1, 'In progress'))}
                          </option>
                          <option value={2}>
                            {intl.formatMessage(listItemMsg('taskstatus', 2, 'Done'))}
                          </option>
                        </select>
                      </div>
                    </div>
                    {viewModel.subTaskTemplateId && viewModel.subTaskTemplateId !== MAIN_TASK_ONLY && (
                      <div className="form-group">
                        <label className="control-label col-md-4">
                          {intl.formatMessage(messages.MarkAsBlocked)}
                        </label>
                        <div className="col-md-8 margin5T">
                          <Checkbox
                            onClick={value =>
                              this.setState({
                                viewModel: {
                                  ...viewModel,
                                  markAsBlocked: value,
                                },
                              })
                            }
                            value={viewModel.markAsBlocked}
                          />
                        </div>
                      </div>
                    )}

                    {viewModel.subTaskTemplateId && viewModel.subTaskTemplateId !== MAIN_TASK_ONLY && (
                      <div className="form-group">
                        <label className="control-label col-md-4">
                          {intl.formatMessage(messages.MarkAsInapplicable)}
                        </label>
                        <div className="col-md-8 margin5T">
                          <Checkbox
                            onClick={value =>
                              this.setState({
                                viewModel: {
                                  ...viewModel,
                                  markAsInapplicable: value,
                                },
                              })
                            }
                            value={viewModel.markAsInapplicable}
                          />
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                )}
                {showReferenceDate ? (
                  <React.Fragment>
                    <div className="form-group">
                      <label className="control-label col-md-4">
                        {intl.formatMessage(messages.ReferenceDate)}
                      </label>
                      <label className="col-md-8">
                        {intl.formatMessage(messages.LastDayOfVatPeriod)}
                      </label>
                    </div>
                    <div className="form-group">
                      <label className="control-label col-md-4">
                        {intl.formatMessage(messages.DaysBefore)}
                      </label>
                      <div className="col-md-2">
                        <input
                          type="number"
                          value={viewModel.toleranceDaysBefore}
                          className="form-control"
                          onChange={e =>
                            this.setState({
                              viewModel: {
                                ...viewModel,
                                toleranceDaysBefore: e.target.value,
                              },
                            })
                          }
                        />
                      </div>
                    </div>
                    <div className="form-group">
                      <label className="control-label col-md-4">
                        {intl.formatMessage(messages.DaysAfter)}
                      </label>
                      <div className="col-md-2">
                        <input
                          type="number"
                          value={viewModel.toleranceDaysAfter}
                          className="form-control"
                          onChange={e =>
                            this.setState({
                              viewModel: {
                                ...viewModel,
                                toleranceDaysAfter: e.target.value,
                              },
                            })
                          }
                        />
                      </div>
                    </div>
                  </React.Fragment>
                ) : null}
                {viewModel.workflowType === 58 && (
                  <div className="form-group">
                    <div className="col-md-offset-4 col-md-8">
                      {intl.formatMessage(messages.GeneratedDocumentQrCodeScannedInfo)}
                    </div>
                  </div>
                )}
              </div>
            </form>
            {isEmailAction &&
              (() => {
                const effectiveLanguage =
                  previewLanguage ||
                  translations?.find(t => t.isTenantLanguage)?.language ||
                  translations?.[0]?.language;
                const previewTexts = {
                  nl: { confirmText: 'Klik op de knop hieronder om te bevestigen.', confirmButton: 'Bevestig' },
                  en: { confirmText: 'Please click the button below to confirm.', confirmButton: 'Confirm' },
                  fr: { confirmText: 'Cliquer sur le bouton ci-dessous pour confirmer.', confirmButton: 'Confirmez' },
                };
                const preview = previewTexts[effectiveLanguage] || previewTexts.en;
                const previewName =
                  translations?.find(t => t.language === effectiveLanguage)?.value || '';
                const companyName = companies.find(c => c.id === previewCompanyId)?.name || '';
                return (
                  <div
                    style={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                    }}
                  >
                    <h4 style={{ marginTop: 0, marginBottom: '12px' }}>
                      {intl.formatMessage(messages.Preview)}
                    </h4>
                    {translations && (
                      <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                        <div style={{ flex: 1 }}>
                          <label className="control-label">
                            {intl.formatMessage(messages.Language)}
                          </label>
                          <select
                            className="form-control"
                            value={effectiveLanguage || ''}
                            onChange={e => this.setState({ previewLanguage: e.target.value })}
                          >
                            {translations.map(t => (
                              <option key={t.language} value={t.language}>
                                {t.language}
                              </option>
                            ))}
                          </select>
                        </div>
                        {companies.length > 1 && (
                          <div style={{ flex: 1 }}>
                            <label className="control-label">
                              {intl.formatMessage(messages.Company)}
                            </label>
                            <select
                              className="form-control"
                              value={previewCompanyId || ''}
                              onChange={e => this.setState({ previewCompanyId: e.target.value })}
                            >
                              {companies.map(c => (
                                <option key={c.id} value={c.id}>
                                  {c.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}
                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        background: '#f5f5f5',
                        borderRadius: '4px',
                        padding: '24px',
                      }}
                    >
                      <div
                        style={{
                          background: '#fff',
                          borderTop: '4px solid #27997e',
                          borderRadius: '4px',
                          padding: '32px 24px',
                          maxWidth: '400px',
                          width: '100%',
                          textAlign: 'center',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        }}
                      >
                        <h2
                          style={{
                            margin: '0 0 8px',
                            fontSize: '20px',
                            fontWeight: 600,
                            wordBreak: 'break-word',
                          }}
                        >
                          {previewName || (
                            <span style={{ color: '#999' }}>
                              {intl.formatMessage(messages.Name)}
                            </span>
                          )}
                        </h2>
                        {companyName && (
                          <p style={{ margin: '0 0 16px', color: '#666', fontSize: '14px' }}>
                            {companyName}
                          </p>
                        )}
                        <p style={{ margin: '0 0 24px', color: '#333', fontSize: '14px' }}>
                          {preview.confirmText}
                        </p>
                        <button
                          type="button"
                          style={{
                            background: '#27997e',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '10px 32px',
                            fontSize: '14px',
                            fontWeight: 600,
                            cursor: 'default',
                          }}
                          disabled
                        >
                          {preview.confirmButton}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}
          </div>
        )}
      </OkCancelModal>
    );
  }
}

export default injectIntl(WorkflowConfigurationModal);
